import type { ModuleManifest } from "@praesto/contracts";
import type { ModuleId } from "@praesto/shared-types";

type ModuleLoader = () => Promise<{ manifest: ModuleManifest }>;

/**
 * Lazy loader for the feature modules. Each module is in a separate chunk.
 *
 * When a tenant has only 'procedure' active, the JS bundle downloaded by
 * the client does NOT contain the code of 'shifts' and 'monitoring'.
 *
 * 'home' is the personal hub: it is always loaded (regardless of tenant
 * activeModules) and rendered first.
 */
const MODULE_LOADERS: Record<ModuleId, ModuleLoader> = {
  home: () => import("@praesto/home"),
  procedure: () => import("@praesto/procedure"),
  shifts: () => import("@praesto/shifts"),
  monitoring: () => import("@praesto/monitoring"),
  reservations: () => import("@praesto/reservations"),
  automation: () => import("@praesto/automation"),
};

export interface LoadActiveModulesOptions {
  /**
   * Called for each active id this bundle has no loader for. The tenant
   * config lives on Firestore and is current at once; the bundle may be the
   * previous release, served by the service worker precache (issue #401).
   * Such an id is skipped so the rest of the app still boots.
   */
  readonly onUnknownModule?: (id: string) => void;
}

export async function loadActiveModules(
  activeModules: ReadonlyArray<ModuleId>,
  options: LoadActiveModulesOptions = {},
): Promise<ReadonlyArray<ModuleManifest>> {
  // Home is always present (personal hub), regardless of tenant activeModules.
  const ids = ["home", ...activeModules.filter((id) => id !== "home")] as ReadonlyArray<ModuleId>;
  const loaders: Array<Promise<ModuleManifest>> = [];
  for (const id of ids) {
    // `Object.hasOwn`: a config value like "toString" must not hit the prototype.
    if (!Object.hasOwn(MODULE_LOADERS, id)) {
      console.warn(`[module-loader] skipping module unknown to this bundle: ${id}`);
      options.onUnknownModule?.(id);
      continue;
    }
    loaders.push(MODULE_LOADERS[id]().then((m) => m.manifest));
  }
  return Promise.all(loaders);
}
